import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

export const SearchPicker = ({
  items,
  value = "",
  values = [],
  multi = false,
  onSelect,
  onToggle,
  placeholder = "Start typing a name…",
  testId,
  allowNone = false,
  noneLabel = "None",
  emptyLabel = "Nothing matches that text.",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) =>
      [i.label, i.hint].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [items, query]);

  const choose = (id) => {
    if (multi) {
      onToggle(id);
      setQuery("");
      return;
    }
    onSelect(id);
    setQuery("");
    setOpen(false);
  };

  const shown = multi ? query : open ? query : selected?.label || "";

  return (
    <div className="relative">
      <Input
        value={shown}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
        className="rounded-sm pr-9"
        data-testid={`${testId}-search`}
      />
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      {open && (
        <div
          className="absolute inset-x-0 top-[42px] z-40 max-h-56 overflow-y-auto border border-slate-300 bg-white shadow-lg"
          data-testid={`${testId}-options`}
        >
          {allowNone && !query.trim() && (
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                choose("");
              }}
              data-testid={`${testId}-option-none`}
              className="block w-full px-4 py-2.5 text-left text-[12px] text-slate-500 transition-colors duration-150 hover:bg-slate-50"
            >
              {noneLabel}
            </button>
          )}
          {matches.length === 0 ? (
            <p className="px-4 py-3 text-[12px] text-slate-500">{emptyLabel}</p>
          ) : (
            matches.map((item) => {
              const picked = multi ? values.includes(item.id) : item.id === value;
              return (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    choose(item.id);
                  }}
                  data-testid={`${testId}-option-${item.id}`}
                  className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-slate-50 ${
                    picked ? "bg-slate-50" : ""
                  }`}
                >
                  <Check
                    className={`h-3.5 w-3.5 shrink-0 ${picked ? "text-slate-900" : "text-transparent"}`}
                    strokeWidth={2.4}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] text-slate-900">{item.label}</span>
                    {item.hint ? (
                      <span className="block truncate text-[11px] text-slate-400">{item.hint}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
